// src/components/GytisBotSettings.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ManualExecuteCard from "./GytisManualExecuteCard";
import { Link } from "react-router-dom";
import { FaWallet } from "react-icons/fa";
import "../styles/futuresBot.css";
import DynamicRuleEditor from "../components/DynamicRuleEditor";
import ConditionalRecRuleEditor from "../botSettings/ConditionalRecRuleEditor";

const isLocal = window.location.hostname === "localhost";
const API_BASE = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
const BOT_KEY = 'gytisbot';

const REC_OPTIONS = ["STRONG BUY", "BUY", "STRONG SELL", "SELL"];
const TREND_OPTIONS = ["Extremely Overextended", "Extremely Oversold", "Overextended", "Oversold", "Bull Run",
     "Bear Trend", "Reversing Up", "Reversing Down", "Consolidating", "Unclear", "Bullish", "Bearish"];
const EXPECTED_OPTIONS = ["Neutral", "Bullish",  "Bearish", "Bullish Low", "Bearish Low", "Bullish Starting", 
    "Bearish Starting", "Bearish Medium", "Bullish Medium", "Bearish High", "Bullish High", "Bearish Possible", "Bullish Possible"];

export default function GytisBotSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [config, setConfig] = useState({
    testMode: true,
    leverage: 10,
    qtyUsd: 50,
    entryTolerancePct: 0.9,
    minScore: 0,
    allowedRecommendations: [],
    allowedTrendPhases: [],
    allowedExpected: [],
    tpAdjustPct: 0,
    slAdjustPct: 0,
    minProjectedGainPct: 0,
    maxAcceptedSlPct: 100,
  });
  const mode = useMemo(() => (config.testMode ? "TEST" : "LIVE"), [config.testMode]);

  async function fetchStatus() {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/api/bots/settings/${BOT_KEY}/status`, { timeout: 15000 });
      setEnabled(Boolean(data?.config?.enabled ?? true));
      if (data?.config) setConfig(c => ({ ...c, ...data.config }));
    } catch (e) {
      console.warn("bitget status error:", e?.response?.data || e.message);
    } finally { setLoading(false); }
  }
  useEffect(() => { fetchStatus(); }, []);

  async function pause(){ await axios.post(`${API_BASE}/api/bots/settings/${BOT_KEY}/pause`); setEnabled(false); }
  async function resume(){ await axios.post(`${API_BASE}/api/bots/settings/${BOT_KEY}/resume`); setEnabled(true); }

  async function save() {
    try {
      setSaving(true);
        const payload = {
          ...config,
          leverage: Number(config.leverage),
          qtyUsd: Number(config.qtyUsd),
          entryTolerancePct: Number(config.entryTolerancePct),
          minScore: Number(config.minScore),
          tpAdjustPct: Number(config.tpAdjustPct),
          slAdjustPct: Number(config.slAdjustPct),
          minProjectedGainPct: Number(config.minProjectedGainPct),
          maxAcceptedSlPct: Number(config.maxAcceptedSlPct),
          dynamicQtyRules: config.dynamicQtyRules || { byScore: [], byMarketCap: [] },
          conditionalRecRules: config.conditionalRecRules || [],
          whitelistOnly: !!config.whitelistOnly,
          symbolWhitelist: (config.symbolWhitelist || []).slice(0, 5),
          symbolQtyOverrides: (config.symbolQtyOverrides || []).slice(0, 5),
        };
        await axios.post(`${API_BASE}/api/bots/settings/${BOT_KEY}/update`, payload, { timeout: 15000 });
      await fetchStatus();
    } catch (e) { alert(`Save failed: ${e.message}`); }
    finally { setSaving(false); }
  }

  const toggleChip = (key, value) => {
    setConfig((c) => {
      const arr = new Set(c[key] || []);
      if (arr.has(value)) arr.delete(value); else arr.add(value);
      return { ...c, [key]: Array.from(arr) };
    });
  };
  const selectAll = key => setConfig(c => ({ ...c, [key]: [] }));
  const clearAll  = key => setConfig(c => ({ ...c, [key]: ["__NONE__"] }));

  // local helpers
  const addOverride = () => setConfig(c => ({
    ...c,
    symbolQtyOverrides: [...(c.symbolQtyOverrides || []), { symbol: "", qtyUsd: 50 }].slice(0,5)
  }));
  const updateOverride = (i, patch) => setConfig(c => {
    const arr = [...(c.symbolQtyOverrides || [])];
    arr[i] = { ...arr[i], ...patch };
    return { ...c, symbolQtyOverrides: arr };
  });
  const removeOverride = (i) => setConfig(c => ({
    ...c,
    symbolQtyOverrides: (c.symbolQtyOverrides || []).filter((_, idx) => idx !== i)
  }));

  const addWhitelist = () => setConfig(c => ({
    ...c,
    symbolWhitelist: [...(c.symbolWhitelist || []), ""].slice(0,5)
  }));
  const updateWhitelist = (i, val) => setConfig(c => {
    const arr = [...(c.symbolWhitelist || [])];
    arr[i] = val;
    return { ...c, symbolWhitelist: arr };
  });
  const removeWhitelist = (i) => setConfig(c => ({
    ...c,
    symbolWhitelist: (c.symbolWhitelist || []).filter((_, idx) => idx !== i)
  }));


  return (
    <div className="fb-page">
      <div className="fb-top">
        <div className="fb-title">BITGET BOT SETTINGS</div>
      </div>

      <div className="fb-card fb-status">
        <div className="fb-status-row">
          <span className={`fb-dot ${enabled ? "on" : "off"}`}></span>
          <span className="fb-status-text">{enabled ? "ON" : "OFF"}</span>
          <div className="fb-spacer" />
          <div style={{ display: "flex", justifyContent: "flex-start"}}>
          <Link to="/gytis-bot-wallet" className="btn ghost" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <FaWallet size={16} />
          </Link>
        </div>
          <button className="btn ghost" disabled={!enabled} onClick={pause}>Pause</button>
          <button className="btn primary" disabled={enabled} onClick={resume}>Resume</button>
        </div>
      </div>

      <div className="fb-card">
        <div className="fb-grid">
          <div className="fb-field">
            <label>Mode</label>
            <select
              value={mode}
              onChange={(e) => setConfig((c) => ({ ...c, testMode: e.target.value !== "LIVE" }))}
            >
              <option>LIVE</option>
              <option>TEST</option>
            </select>
          </div>

          <div className="fb-field">
            <label>Leverage (x)</label>
            <input
              type="number"
              min="1"
              value={config.leverage}
              onChange={(e) => setConfig((c) => ({ ...c, leverage: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>USD per trade</label>
            <input
              type="number"
              min="1"
              value={config.qtyUsd}
              onChange={(e) => setConfig((c) => ({ ...c, qtyUsd: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>Entry tolerance (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={config.entryTolerancePct}
              onChange={(e) => setConfig((c) => ({ ...c, entryTolerancePct: e.target.value }))}
            />
          </div>

          <div className="fb-field">
            <label>Minimum score</label>
            <input
              type="number"
              step="0.1"
              value={config.minScore}
              onChange={(e) => setConfig((c) => ({ ...c, minScore: e.target.value }))}
            />
          </div>
        </div>

        <div className="fb-grid" style={{ marginTop: 12 }}>
          <div className="fb-field">
            <label>TP adjust (%)</label>
            <input
              type="number"
              step="0.1"
              value={config.tpAdjustPct}
              onChange={(e) => setConfig(c => ({ ...c, tpAdjustPct: e.target.value }))}
            />
            <div className="hint">Negative reduces TP (e.g. -1 ⇒ 1% lower TP).</div>
          </div>

          <div className="fb-field">
            <label>SL adjust (%)</label>
            <input
              type="number"
              step="0.1"
              value={config.slAdjustPct}
              onChange={(e) => setConfig(c => ({ ...c, slAdjustPct: e.target.value }))}
            />
            <div className="hint">Negative tightens SL (closer to entry).</div>
          </div>

          <div className="fb-field">
            <label>Minimum projected gain (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={config.minProjectedGainPct}
              onChange={(e) => setConfig(c => ({ ...c, minProjectedGainPct: e.target.value }))}
            />
            <div className="hint">Skip trades with entry→TP gain below this.</div>
          </div>

          <div className="fb-field">
            <label>Maximum SL risk (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={config.maxAcceptedSlPct}
              onChange={(e) => setConfig(c => ({ ...c, maxAcceptedSlPct: e.target.value }))}
            />
            <div className="hint">Skip trades if |entry–SL|/entry exceeds this.</div>
          </div>
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Per-Symbol USD Overrides (Bypass All Rules)</div>
            <div className="fb-actions">
              <button className="link" onClick={addOverride} disabled={(config.symbolQtyOverrides?.length || 0) >= 5}>Add</button>
              <button className="link" onClick={() => setConfig(c => ({ ...c, symbolQtyOverrides: [] }))}>Clear</button>
            </div>
          </div>

          {(config.symbolQtyOverrides || []).length === 0 && (
            <div className="fb-hint">Add up to 5 symbols. Trades on these will bypass all filters and use the specified USD.</div>
          )}

          {(config.symbolQtyOverrides || []).map((r, i) => (
            <div key={i} className="fb-grid" style={{ marginBottom: 8 }}>
              <div className="fb-field">
                <label>Symbol</label>
                <input
                  placeholder="e.g. BTC, BTCUSDT, BTC/USDT"
                  value={r.symbol || ""}
                  onChange={e => updateOverride(i, { symbol: e.target.value })}
                />
              </div>
              <div className="fb-field">
                <label>USD</label>
                <input
                  type="number"
                  min="1"
                  value={r.qtyUsd ?? ""}
                  onChange={e => updateOverride(i, { qtyUsd: Number(e.target.value) })}
                />
              </div>
              <div className="fb-field" style={{ alignSelf: 'end' }}>
                <button className="btn ghost" onClick={() => removeOverride(i)}>Remove</button>
              </div>
            </div>
          ))}
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Whitelist-Only Symbols</div>
            <div className="fb-actions">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={!!config.whitelistOnly}
                  onChange={e => setConfig(c => ({ ...c, whitelistOnly: e.target.checked }))}
                />
                <span>Whitelist-only</span>
              </label>
              <button className="link" onClick={addWhitelist} disabled={(config.symbolWhitelist?.length || 0) >= 5}>Add</button>
              <button className="link" onClick={() => setConfig(c => ({ ...c, symbolWhitelist: [] }))}>Clear</button>
            </div>
          </div>

          {(config.symbolWhitelist || []).length === 0 && (
            <div className="fb-hint">Add up to 5 symbols. When <b>Whitelist-only</b> is ON, the bot will trade only these.</div>
          )}

          {(config.symbolWhitelist || []).map((sym, i) => (
            <div key={i} className="fb-grid" style={{ marginBottom: 8 }}>
              <div className="fb-field" style={{ gridColumn: 'span 2' }}>
                <label>Symbol</label>
                <input
                  placeholder="e.g. BTC, BTCUSDT, BTC/USDT"
                  value={sym || ""}
                  onChange={e => updateWhitelist(i, e.target.value)}
                />
              </div>
              <div className="fb-field" style={{ alignSelf: 'end' }}>
                <button className="btn ghost" onClick={() => removeWhitelist(i)}>Remove</button>
              </div>
            </div>
          ))}
        </div>


        <DynamicRuleEditor
          label="Score-based Rules"
          rules={config.dynamicQtyRules?.byScore || []}
          setRules={(newRules) =>
            setConfig(c => ({
              ...c,
              dynamicQtyRules: { ...(c.dynamicQtyRules || {}), byScore: newRules }
            }))
          }
        />

        <DynamicRuleEditor
          label="MarketCap-based Rules"
          rules={config.dynamicQtyRules?.byMarketCap || []}
          setRules={(newRules) =>
            setConfig(c => ({
              ...c,
              dynamicQtyRules: { ...(c.dynamicQtyRules || {}), byMarketCap: newRules }
            }))
          }
        />

        <ConditionalRecRuleEditor
          rules={config.conditionalRecRules || []}
          setRules={(newRules) => setConfig(c => ({ ...c, conditionalRecRules: newRules }))}
        />


        {/* chips */}
        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Recommendations</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedRecommendations")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedRecommendations")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {REC_OPTIONS.map((o) => {
              const active = (config.allowedRecommendations || []).includes(o);
              // [] means "allow all" -> show as neutral chips; we still let user pre-select
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedRecommendations", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedRecommendations?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Trend Phase</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedTrendPhases")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedTrendPhases")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {TREND_OPTIONS.map((o) => {
              const active = (config.allowedTrendPhases || []).includes(o);
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedTrendPhases", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedTrendPhases?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-section">
          <div className="fb-section-head">
            <div className="fb-section-title">Expected Direction (24h)</div>
            <div className="fb-actions">
              <button className="link" onClick={() => selectAll("allowedExpected")}>Allow all</button>
              <button className="link" onClick={() => clearAll("allowedExpected")}>Block all</button>
            </div>
          </div>
          <div className="chip-row">
            {EXPECTED_OPTIONS.map((o) => {
              const active = (config.allowedExpected || []).includes(o);
              return (
                <button
                  key={o}
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => toggleChip("allowedExpected", o)}
                >
                  {o}
                </button>
              );
            })}
          </div>
          <div className="hint">{(config.allowedExpected?.length ?? 0) === 0 ? "Allowing all" : "Filtering to selected"}</div>
        </div>

        <div className="fb-actions-row">
          <button className="btn ghost" onClick={fetchStatus} disabled={loading || saving}>
            Reset
          </button>
          <div className="fb-spacer" />
          <button className="btn primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
        <ManualExecuteCard apiBase={API_BASE} />
      </div>
    </div>
  );
}